import {
	Calculator,
	CheckCircle2,
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	Copy,
	DollarSign,
	Eye,
	EyeOff,
	Package,
	QrCode,
	Search,
	User,
} from "lucide-react";
import { Card } from "../../../../ui/Card";
import { Button } from "../../../../ui/Button";
import { quotationFooter } from "../quotationFooterStyles";
import type { Cliente } from "../../../../../types/gerArquivos";
import type { OrderObject } from "../../types";
import type { Quality } from "../QualitySelectModal";
import type { CalculateQuotationResult } from "../../services/quotationService";

interface QuotationClientSectionProps {
	compact?: boolean;
	useManualClientName: boolean;
	selectedClient: Cliente | null;
	clientSearchQuery: string;
	manualClientName: string;
	carregandoClientes: boolean;
	clientes: Cliente[];
	/** Modo "cliente de outra unidade" (roteado) ativo. */
	routedMode?: boolean;
	/** Erro ao preparar o contexto roteado (token exchange). */
	routingError?: string | null;
	/** Token exchange em andamento (bloqueia seleção). */
	preparingRouting?: boolean;
	onClientSearchChange: (value: string) => void;
	onManualClientNameChange: (value: string) => void;
	onToggleManualName: (enabled: boolean) => void;
	onToggleRoutedMode?: (enabled: boolean) => void;
	onSelectClient: (client: Cliente) => void;
	onClearClient: () => void;
}

interface QuotationProductHeaderProps {
	compact?: boolean;
	productName: string;
	selectedQuality: Quality | null;
	isMetros: boolean;
}

interface QuotationCalculationCardProps {
	compact?: boolean;
	isCalculating: boolean;
	error: string | null;
	quotationResult: CalculateQuotationResult | null;
	fonteCalculo: CalculateQuotationResult["fonte"] | null;
	valorTotalFinal: number;
	precoFinal: number;
	isMetros: boolean;
	totalMetragem: number;
	totalUnidades: number;
	finalMetragem: number | null;
	isEditingMetragem: boolean;
	editedMetragemString: string;
	editedMetragem: number | null;
	confirmedMetragem: number | null;
	minMetragem: number;
	descontoAplicado: number;
	onEditedMetragemChange: (value: string) => void;
	onEditedMetragemBlur: () => void;
	onToggleEditingMetragem: (enabled: boolean) => void;
	onConfirmEditedMetragem: () => void;
}

interface QuotationObjectsSectionProps {
	compact?: boolean;
	objects: OrderObject[];
	showObjects: boolean;
	paginatedObjects: OrderObject[];
	isMetros: boolean;
	currentPage: number;
	totalPages: number;
	itemsPerPage: number;
	indexOfFirstItem: number;
	indexOfLastItem: number;
	onToggleShowObjects: (show: boolean) => void;
	onChangeItemsPerPage: (value: number) => void;
	onChangePage: (page: number) => void;
}

interface QuotationSummaryFooterProps {
	compact?: boolean;
	quotationResult: CalculateQuotationResult | null;
	copiedFeedback: boolean;
	isCreatingOrder: boolean;
	hasSelectedClient: boolean;
	canCreateOrder: boolean;
	onCopyBaseSummary: () => void;
	onCreateOrder: () => void;
	onNewQuotation: () => void;
}

interface SummaryToggleProps {
	id?: string;
	checked: boolean;
	onChange: (enabled: boolean) => void;
	caption: string;
	ariaLabel: string;
	compact?: boolean;
	accent?: "blue" | "green";
	align?: "start" | "end";
}

function SummaryToggle({
	id,
	checked,
	onChange,
	caption,
	ariaLabel,
	compact = false,
	accent = "blue",
	align = "end",
}: SummaryToggleProps) {
	const accentClasses =
		accent === "green"
			? "peer-focus:ring-green-400/50 peer-checked:bg-green-500"
			: "peer-focus:ring-blue-400/50 peer-checked:bg-blue-500";

	return (
		<label
			htmlFor={id}
			className={`flex flex-col ${align === "start" ? "items-start" : "items-end"} gap-0 cursor-pointer flex-shrink-0`}
		>
			<input
				id={id}
				type="checkbox"
				checked={checked}
				onChange={(e) => onChange(e.target.checked)}
				aria-label={ariaLabel}
				className="sr-only peer"
			/>
			<div
				className={`relative inline-flex ${compact ? "w-9 h-5 after:h-3.5 after:w-3.5" : "w-10 h-5.5 after:h-4.5 after:w-4.5"} bg-navy-700 peer-focus:outline-none peer-focus:ring-2 rounded-full peer after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:transition-all peer-checked:after:translate-x-full peer-checked:after:border-white rtl:peer-checked:after:-translate-x-full ${accentClasses}`}
			/>
			<span
				className={`font-medium uppercase tracking-[0.16em] text-gray-500 ${compact ? "text-[8px]" : "text-[9px]"}`}
			>
				{caption}
			</span>
		</label>
	);
}

export function QuotationClientSection({
	compact = false,
	useManualClientName,
	selectedClient,
	clientSearchQuery,
	manualClientName,
	carregandoClientes,
	clientes,
	routedMode = false,
	routingError = null,
	preparingRouting = false,
	onClientSearchChange,
	onManualClientNameChange,
	onToggleManualName,
	onToggleRoutedMode,
	onSelectClient,
	onClearClient,
}: QuotationClientSectionProps) {
	// Em modo roteado, a lista mostra só clientes de outra unidade (roteados).
	const visibleClientes = routedMode
		? clientes.filter((c) => c.isRouted)
		: clientes;
	const showNoRoutedHint =
		routedMode &&
		!carregandoClientes &&
		clientSearchQuery.trim().length >= 2 &&
		visibleClientes.length === 0;
	return (
		<Card
			title="Cliente"
			description={
				compact ? undefined : "Selecione um cliente ou digite o nome manualmente"
			}
			className={`bg-navy-800/35 border-navy-600/40 ${compact ? "p-2" : "p-3 sm:p-3"}`}
			headerClassName={compact ? "mb-0.5" : "mb-2"}
			titleClassName={compact ? "text-[13px]" : "text-sm"}
			descriptionClassName="mt-0.5 text-[11px] leading-4 sm:text-xs"
		>
			<div className={compact ? "space-y-1" : "space-y-2"}>
				<div className="flex items-center gap-2">
					{useManualClientName ? (
						<div className="relative flex-1">
							<User className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
							<input
								type="text"
								value={manualClientName}
								onChange={(e) => onManualClientNameChange(e.target.value)}
								placeholder="Nome do cliente"
								className={`w-full pl-8 pr-3 ${compact ? "py-1.5 text-[13px]" : "py-2 text-sm"} bg-navy-900/60 border border-navy-600/60 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-400/50 focus:border-transparent`}
							/>
						</div>
					) : selectedClient ? (
						<div className={`flex-1 px-3 ${compact ? "py-1.5" : "py-2"} bg-blue-500/10 border border-blue-500/20 rounded-xl shadow-inner shadow-blue-500/5`}>
							<div className="flex items-center justify-between gap-2">
								<div className="flex-1 min-w-0 flex items-center gap-2">
									<div className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-500/15 border border-blue-400/20 flex-shrink-0">
										<User className="w-3 h-3 text-blue-300" />
									</div>
									<div className="min-w-0">
										<div className="text-[13px] text-blue-50 font-semibold truncate">
											{selectedClient.empresa || selectedClient.nome}
										</div>
										{selectedClient.isRouted ? (
											<div className="text-[10px] text-green-300/80 truncate">
												Unidade: {selectedClient.targetTenantId}
											</div>
										) : selectedClient.whatsapp ? (
											<div className="text-[10px] text-blue-200/65 truncate">
												{selectedClient.whatsapp}
											</div>
										) : null}
									</div>
								</div>
								<Button
									variant="ghost"
									size="sm"
									type="button"
									onMouseDown={(event) => {
										event.preventDefault();
										event.stopPropagation();
									}}
									onClick={(event) => {
										event.preventDefault();
										event.stopPropagation();
										onClearClient();
									}}
									className="h-7 rounded-lg border border-white/10 bg-white/5 px-2.5 text-[10px] text-blue-100 hover:bg-white/10 hover:text-white"
								>
									Alterar
								</Button>
							</div>
						</div>
					) : (
						<div className="relative flex-1">
							<Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
							<input
								type="text"
								value={clientSearchQuery}
								onChange={(e) => onClientSearchChange(e.target.value)}
								placeholder={
									routedMode
										? "Buscar cliente de outra unidade"
										: "Buscar cliente por nome, WhatsApp ou CNPJ/CPF"
								}
								className={`w-full pl-8 pr-7 ${compact ? "py-1.5 text-[13px]" : "py-2 text-sm"} bg-navy-900/60 border ${routedMode ? "border-green-500/40" : "border-navy-600/60"} rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-400/50 focus:border-transparent`}
							/>
							{carregandoClientes && (
								<div className="absolute right-2 top-1/2 -translate-y-1/2">
									<div className="w-3.5 h-3.5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
								</div>
							)}
						</div>
					)}

					{onToggleRoutedMode && !useManualClientName && (
						<SummaryToggle
							checked={routedMode}
							onChange={onToggleRoutedMode}
							caption="Unidade"
							ariaLabel="Buscar cliente de outra unidade"
							compact={compact}
							accent="green"
						/>
					)}
					{!routedMode && (
						<SummaryToggle
							checked={useManualClientName}
							onChange={onToggleManualName}
							caption="Manual"
							ariaLabel="Alternar nome manual do cliente"
							compact={compact}
							accent="blue"
						/>
					)}
				</div>

				{routingError && (
					<p className="text-red-400 text-[11px] px-1">{routingError}</p>
				)}
				{preparingRouting && (
					<p className="text-green-300/80 text-[11px] px-1">
						Preparando cliente de outra unidade...
					</p>
				)}

				{!useManualClientName && !selectedClient && visibleClientes.length > 0 && (
					<div className="max-h-32 overflow-y-auto bg-navy-900/55 border border-navy-600/50 rounded-xl divide-y divide-navy-700/60">
						{visibleClientes.map((cliente) => (
							<button
								key={
									cliente.isRouted
										? `${cliente.targetTenantId}-${cliente.id}`
										: cliente.id
								}
								type="button"
								disabled={preparingRouting}
								onClick={() => onSelectClient(cliente)}
								className="w-full px-3 py-1.5 text-left hover:bg-navy-800/80 transition-colors disabled:opacity-50"
							>
								<div className="text-sm text-gray-100 font-medium truncate">
									{cliente.empresa || cliente.nome}
								</div>
								{cliente.isRouted ? (
									<div className="text-[11px] text-green-300/80 mt-0.5 truncate">
										Unidade: {cliente.targetTenantId}
									</div>
								) : cliente.whatsapp ? (
									<div className="text-[11px] text-gray-400 mt-0.5 truncate">
										WhatsApp: {cliente.whatsapp}
									</div>
								) : null}
							</button>
						))}
					</div>
				)}

				{showNoRoutedHint && (
					<p className="text-gray-400 text-[11px] px-1 py-1">
						Nenhum cliente de outra unidade encontrado.
					</p>
				)}
			</div>
		</Card>
	);
}

export function QuotationProductHeader({
	compact = false,
	productName,
	selectedQuality,
	isMetros,
}: QuotationProductHeaderProps) {
	return (
		<Card className={`bg-navy-800/35 border-navy-600/40 ${compact ? "p-2" : "p-2.5 sm:p-3"}`}>
			<div className={compact ? "space-y-1" : "space-y-2"}>
				<div>
					<div className={`mb-0.5 uppercase tracking-[0.22em] text-gray-500 ${compact ? "text-[8px]" : "text-[9px]"}`}>
						Servico selecionado
					</div>
					<div className={`${compact ? "text-[14px]" : "text-base sm:text-lg"} text-white font-semibold leading-tight`}>
						{productName}
					</div>
				</div>

				<div className={`flex flex-wrap items-center ${compact ? "gap-1.5" : "gap-2"}`}>
					{selectedQuality && (
						<div className={`inline-flex items-center gap-1.5 ${compact ? "px-2 py-0.5" : "px-2.5 py-1"} bg-blue-500/15 border border-blue-500/30 rounded-xl`}>
							<Package className="w-3 h-3 text-blue-400 flex-shrink-0" />
							<span className={`${compact ? "text-[11px]" : "text-xs"} text-blue-200 font-semibold`}>
								{selectedQuality.one1_nome}
							</span>
						</div>
					)}

					<div
						className={`inline-flex items-center gap-1.5 ${compact ? "px-2 py-0.5" : "px-2.5 py-1"} rounded-xl border ${
							isMetros
								? "bg-green-500/15 border-green-500/30"
								: "bg-sky-500/15 border-sky-500/30"
						}`}
					>
						<Calculator
							className={`w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0 ${
								isMetros ? "text-green-400" : "text-sky-400"
							}`}
						/>
						<span
							className={`${compact ? "text-[11px]" : "text-xs"} font-semibold ${
								isMetros ? "text-green-200" : "text-sky-200"
							}`}
						>
							{isMetros ? "Metros lineares" : "Unidades"}
						</span>
					</div>
				</div>
			</div>
		</Card>
	);
}

export function QuotationCalculationCard({
	compact = false,
	isCalculating,
	error,
	quotationResult,
	fonteCalculo,
	valorTotalFinal,
	precoFinal,
	isMetros,
	totalMetragem,
	totalUnidades,
	finalMetragem,
	isEditingMetragem,
	editedMetragemString,
	editedMetragem,
	confirmedMetragem,
	minMetragem,
	descontoAplicado,
	onEditedMetragemChange,
	onEditedMetragemBlur,
	onToggleEditingMetragem,
	onConfirmEditedMetragem,
}: QuotationCalculationCardProps) {
	if (isCalculating) {
		return (
			<Card>
				<div className="flex items-center gap-2 text-sm text-gray-300">
					<div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
					Calculando orcamento...
				</div>
			</Card>
		);
	}

	if (error) {
		return (
			<Card>
				<div className="p-2 bg-red-600/10 border border-red-600/20 rounded-lg">
					<p className="text-xs text-red-200 font-medium">Erro ao calcular</p>
					<p className="text-[10px] text-red-100 mt-1">{error}</p>
				</div>
			</Card>
		);
	}

	if (!quotationResult) return null;

	const metricDisplay =
		finalMetragem !== null
			? finalMetragem
			: isMetros
				? totalMetragem
				: totalUnidades;

	return (
		<Card className="bg-navy-800/35 border-navy-600/40">
			<div className={`${compact ? "p-2 space-y-2" : "p-3 sm:p-4 space-y-3"} bg-gradient-to-br from-green-600/18 via-emerald-600/14 to-teal-500/10 border border-green-500/30 rounded-2xl shadow-lg shadow-green-500/10`}>
				<div className="flex flex-wrap items-center justify-between gap-1.5">
					<div className="flex items-center gap-2">
						<div className={`${compact ? "p-0.5" : "p-0.5 sm:p-1"} bg-green-500/20 rounded-full`}>
							<DollarSign className={`${compact ? "w-3 h-3" : "w-3 h-3 sm:w-4 sm:h-4"} text-green-400`} />
						</div>
						<div className={`${compact ? "text-[8px]" : "text-[10px] sm:text-xs"} text-green-300 font-semibold uppercase tracking-wide`}>
							Resultado do Calculo
						</div>
					</div>

					{fonteCalculo && (
						<div
							className={`inline-flex items-center gap-1.5 ${compact ? "px-2 py-1" : "px-3 py-2"} rounded-xl ${
								fonteCalculo === "discount"
									? "bg-blue-600/20 border border-blue-500/40"
									: fonteCalculo === "escalonamento"
										? "bg-yellow-500/20 border border-yellow-500/50"
										: "bg-gray-600/20 border border-gray-500/40"
							}`}
						>
							{fonteCalculo === "discount" ? (
								<div className="flex items-center gap-2">
									<div className="p-1 bg-blue-500/20 rounded-full">
										<Package className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-400" />
									</div>
									<div className={`${compact ? "text-[8px]" : "text-[10px]"} text-blue-300/90 font-semibold uppercase tracking-wide`}>
										Desconto do Cliente Aplicado
									</div>
								</div>
							) : fonteCalculo === "escalonamento" ? (
								<div className="flex items-center gap-2">
									<div className="p-1 bg-yellow-500/30 rounded-full">
										<Calculator className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-yellow-300" />
									</div>
									<div className={`${compact ? "text-[8px]" : "text-[10px]"} text-yellow-200 font-semibold uppercase tracking-wide`}>
										Escalonamento Aplicado
									</div>
								</div>
							) : (
								<div className="flex items-center gap-2">
									<div className="p-1 bg-gray-500/20 rounded-full">
										<DollarSign className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" />
									</div>
									<div className={`${compact ? "text-[8px]" : "text-[10px]"} text-gray-300/90 font-semibold uppercase tracking-wide`}>
										Preco Base
									</div>
								</div>
							)}
						</div>
				)}
				</div>

				<div className={`bg-green-600/16 border border-green-500/25 rounded-2xl ${compact ? "p-2.5" : "p-4 sm:p-5"}`}>
					<div className={`${compact ? "text-[10px] mb-1" : "text-xs sm:text-sm mb-2"} text-green-300/90 font-medium uppercase tracking-wide`}>
						Valor Total
					</div>
					<div className={`${compact ? "text-[17px]" : "text-2xl sm:text-3xl md:text-4xl"} font-bold text-green-50 leading-tight`}>
						R${" "}
						{valorTotalFinal.toLocaleString("pt-BR", {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2,
						})}
					</div>
				</div>

				<div className={`grid ${compact ? "grid-cols-2 gap-1.5 pt-0.5" : "grid-cols-1 sm:grid-cols-2 gap-3 pt-1"}`}>
					<div className={`rounded-xl border border-white/6 bg-navy-900/20 ${compact ? "p-2" : "p-3"} space-y-1`}>
						<div className={`${compact ? "text-[10px] leading-3" : "text-xs sm:text-sm"} text-green-300/75 font-medium`}>
							Preco {isMetros ? "por metro linear" : "por unidade"}
						</div>
						<div className={`${compact ? "text-[13px]" : "text-base sm:text-lg"} font-bold text-green-50`}>
							R${" "}
							{precoFinal.toLocaleString("pt-BR", {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2,
							})}
						</div>
					</div>
					<div className={`rounded-xl border border-white/6 bg-navy-900/20 ${compact ? "p-2" : "p-3"} space-y-1`}>
						<div className={`${compact ? "text-[10px] leading-3" : "text-xs sm:text-sm"} text-green-300/75 font-medium`}>
							{isMetros ? "Metragem" : "Unidades"}
						</div>
						{isEditingMetragem ? (
							<div className="flex items-baseline gap-1.5">
								<input
									type="text"
									inputMode="decimal"
									value={editedMetragemString}
									onChange={(e) => onEditedMetragemChange(e.target.value)}
									onBlur={onEditedMetragemBlur}
									className="w-24 sm:w-28 px-2 py-1 bg-green-600/30 border border-green-400/50 rounded text-sm sm:text-base text-white font-bold focus:outline-none focus:ring-1 focus:ring-green-400 focus:border-green-400"
									placeholder={
										isMetros ? totalMetragem.toFixed(2).replace(".", ",") : totalUnidades.toString()
									}
								/>
								<span className="text-xs text-green-200/80">
									{isMetros ? "m" : "un."}
								</span>
							</div>
						) : (
							<div className={`${compact ? "text-[13px] leading-4" : "text-base sm:text-lg"} font-bold text-green-50`}>
								{isMetros
									? `${metricDisplay.toFixed(2)} m`
									: `${metricDisplay} un.`}
							</div>
						)}
					</div>
				</div>

				<div className={`${compact ? "pt-1" : "pt-2"} border-t border-green-500/20`}>
					<div className={`flex items-center justify-between gap-3 rounded-xl border border-green-500/15 bg-navy-900/20 ${compact ? "px-2 py-1.5" : "px-3 py-2"}`}>
						<div className="min-w-0">
							<div className={`${compact ? "text-[10px] leading-3" : "text-xs sm:text-sm"} text-green-200 font-medium`}>
								Editar manualmente
							</div>
							<div className={`${compact ? "text-[9px]" : "text-[11px]"} text-green-300/65`}>
								Ligue para ajustar a metragem e recalcular o valor.
							</div>
						</div>
						<div className="flex items-center gap-2 flex-shrink-0">
							{isEditingMetragem &&
								editedMetragem !== null &&
								editedMetragem !== confirmedMetragem && (
									<Button
										variant="ghost"
										size="sm"
										onClick={onConfirmEditedMetragem}
										className="text-[10px] px-2 py-1 h-auto bg-green-500/20 hover:bg-green-500/30 text-green-300 border border-green-500/40"
									>
										Recalcular
									</Button>
								)}
							<SummaryToggle
								checked={isEditingMetragem}
								onChange={onToggleEditingMetragem}
								caption="Editar"
								ariaLabel="Alternar edicao manual da metragem"
								compact={compact}
								accent="green"
							/>
						</div>
					</div>
				</div>

				{quotationResult.desconto &&
					quotationResult.desconto.has_discount &&
					descontoAplicado > 0 && (
						<div className="pt-1.5 border-t border-green-500/20">
							<div className="flex items-center gap-2 p-2 bg-blue-600/10 border border-blue-600/20 rounded-lg">
								<Package className="w-3 h-3 text-blue-400" />
								<div className="flex-1">
									<div className="text-[9px] text-blue-300/80 font-medium">
										Desconto aplicado
									</div>
									<div className="text-xs text-blue-100 font-semibold">
										R${" "}
										{descontoAplicado.toLocaleString("pt-BR", {
											minimumFractionDigits: 2,
											maximumFractionDigits: 2,
										})}{" "}
										<span className="text-[10px] text-blue-300/70">
											(de R$ {quotationResult.desconto.product_base_price.toFixed(2)} por
											R$ {quotationResult.desconto.final_price.toFixed(2)})
										</span>
									</div>
								</div>
							</div>
						</div>
					)}
			</div>
		</Card>
	);
}

export function QuotationObjectsSection({
	compact = false,
	objects,
	showObjects,
	paginatedObjects,
	isMetros,
	currentPage,
	totalPages,
	itemsPerPage,
	indexOfFirstItem,
	indexOfLastItem,
	onToggleShowObjects,
	onChangeItemsPerPage,
	onChangePage,
}: QuotationObjectsSectionProps) {
	return (
		<Card
			title="Objetos Coletados"
			description={
				compact
					? `${objects.length} ${objects.length === 1 ? "item" : "itens"}`
					: showObjects
						? `${objects.length} objeto(s) da selecao`
						: undefined
			}
			className={compact ? "p-2" : ""}
			headerClassName={compact ? "mb-1" : ""}
			titleClassName={compact ? "text-[13px]" : ""}
			descriptionClassName={compact ? "mt-0 text-[10px] leading-3" : ""}
		>
			<div className={compact ? "mb-0" : "mb-3 pb-3 border-b border-navy-700/50"}>
				{!showObjects ? (
					<button
						onClick={() => onToggleShowObjects(true)}
						className={`flex items-center gap-2 w-full ${compact ? "px-2.5 py-1.5 text-[11px]" : "px-3 py-2 text-xs"} bg-navy-700/50 hover:bg-navy-700 border border-navy-600/50 rounded-lg transition-colors text-gray-300 hover:text-white`}
					>
						<Eye className={compact ? "w-3.5 h-3.5" : "w-4 h-4"} />
						<span>{compact ? `Mostrar lista (${objects.length})` : `Mostrar objetos coletados (${objects.length})`}</span>
					</button>
				) : (
					<button
						onClick={() => onToggleShowObjects(false)}
						className={`flex items-center gap-2 ${compact ? "px-1 py-0.5 text-[11px]" : "px-2 py-1 text-xs"} text-gray-400 hover:text-gray-200 transition-colors`}
					>
						<EyeOff className={compact ? "w-3 h-3" : "w-3.5 h-3.5"} />
						<span>Ocultar objetos</span>
					</button>
				)}
			</div>

			{showObjects && (
				<>
					<div className="space-y-2 sm:space-y-2.5 md:space-y-3">
						{paginatedObjects.length > 0 ? (
							paginatedObjects.map((obj, index) => {
								const globalIndex = indexOfFirstItem + index;
								return (
									<div
										key={obj.id || globalIndex}
										className="flex items-center justify-between p-3 sm:p-3.5 md:p-4 lg:p-5 bg-navy-800/50 rounded-lg border border-navy-700/50 hover:bg-navy-800/70 hover:border-navy-600/70 transition-all shadow-sm hover:shadow-md"
									>
										<div className="flex-1 min-w-0 pr-3 sm:pr-4">
											<div className="text-sm sm:text-base md:text-lg font-semibold text-white truncate mb-1">
												{obj.nome}
											</div>
											<div className="text-xs sm:text-sm md:text-base text-gray-300 font-medium">
												{obj.width.toFixed(2)}cm x {obj.height.toFixed(2)}cm
												{obj.quantity > 1 && (
													<span className="ml-1 text-gray-400">
														x {obj.quantity}
													</span>
												)}
											</div>
										</div>
										<div className="text-sm sm:text-base md:text-lg lg:text-xl text-blue-400 font-bold flex-shrink-0 ml-3 sm:ml-4 px-2 sm:px-3 py-1 sm:py-1.5 bg-blue-500/10 rounded border border-blue-500/20">
											{isMetros
												? `${((obj.height * obj.quantity) / 100).toFixed(2)}m`
												: `${obj.quantity} un.`}
										</div>
									</div>
								);
							})
						) : (
							<div className="p-6 text-center text-gray-400 text-sm sm:text-base">
								Nenhum objeto encontrado
							</div>
						)}
					</div>

					{objects.length > itemsPerPage && (
						<div className="mt-2 sm:mt-3 md:mt-4 pt-2 sm:pt-3 border-t border-navy-700/50">
							<div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-3">
								<div className="text-[9px] sm:text-[10px] md:text-xs text-gray-400 flex-shrink-0 order-2 sm:order-1">
									<span className="hidden md:inline">
										{indexOfFirstItem + 1}-{indexOfLastItem} de {objects.length} objetos
									</span>
									<span className="md:hidden">
										Pagina {currentPage} de {totalPages}
									</span>
								</div>

								<div className="flex items-center gap-1.5 sm:gap-2 flex-1 justify-between sm:justify-end min-w-0 order-1 sm:order-2">
									<div className="relative flex-shrink-0">
										<select
											value={itemsPerPage}
											onChange={(e) => onChangeItemsPerPage(Number(e.target.value))}
											className="px-1.5 sm:px-2 md:px-2.5 py-1 sm:py-1.5 pr-5 sm:pr-6 md:pr-7 bg-navy-800 rounded text-[9px] sm:text-[10px] md:text-xs text-blue-400 appearance-none cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-400/30 border border-blue-500/40 hover:border-blue-500/60 transition-colors"
											title="Itens por pagina"
										>
											<option value={3}>3</option>
											<option value={5}>5</option>
											<option value={10}>10</option>
											<option value={15}>15</option>
											<option value={20}>20</option>
										</select>
										<ChevronDown className="w-2.5 h-2.5 sm:w-3 sm:h-3 absolute right-1 top-[50%] -translate-y-1/2 text-gray-400 pointer-events-none" />
									</div>

									<div className="flex items-center gap-0.5 sm:gap-1">
										<button
											onClick={() => onChangePage(Math.max(1, currentPage - 1))}
											disabled={currentPage === 1}
											className={`p-0.5 sm:p-1 md:p-1.5 rounded transition-colors ${
												currentPage === 1
													? "text-gray-600 cursor-not-allowed opacity-50"
													: "text-gray-300 hover:bg-navy-800 hover:text-blue-400"
											}`}
											title="Pagina anterior"
											aria-label="Pagina anterior"
										>
											<ChevronLeft className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4" />
										</button>

										<div className="flex items-center gap-0.5">
											{getVisiblePages(totalPages, currentPage).map((page, index) => {
												if (page === "...") {
													return (
														<span
															key={`ellipsis-${index}`}
															className="px-0.5 sm:px-1 text-[9px] sm:text-[10px] text-gray-500"
														>
															...
														</span>
													);
												}

												const isActive = currentPage === page;
												return (
													<button
														key={page}
														onClick={() => onChangePage(page)}
														className={`min-w-[20px] sm:min-w-[24px] md:min-w-[28px] h-5 sm:h-6 md:h-7 px-1 sm:px-1.5 md:px-2 flex items-center justify-center rounded text-[9px] sm:text-[10px] md:text-xs font-medium transition-colors ${
															isActive
																? "bg-blue-400 text-navy-900 font-bold"
																: "text-gray-300 hover:bg-navy-800 hover:text-blue-400"
														}`}
														title={`Ir para pagina ${page}`}
														aria-label={`Pagina ${page}`}
														aria-current={isActive ? "page" : undefined}
													>
														{page}
													</button>
												);
											})}
										</div>

										<button
											onClick={() => onChangePage(Math.min(totalPages, currentPage + 1))}
											disabled={currentPage === totalPages}
											className={`p-0.5 sm:p-1 md:p-1.5 rounded transition-colors ${
												currentPage === totalPages
													? "text-gray-600 cursor-not-allowed opacity-50"
													: "text-gray-300 hover:bg-navy-800 hover:text-blue-400"
											}`}
											title="Proxima pagina"
											aria-label="Proxima pagina"
										>
											<ChevronRight className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4" />
										</button>
									</div>
								</div>
							</div>
						</div>
					)}
				</>
			)}
		</Card>
	);
}

export function QuotationSummaryFooter({
	compact = false,
	quotationResult,
	copiedFeedback,
	isCreatingOrder,
	hasSelectedClient,
	canCreateOrder,
	onCopyBaseSummary,
	onCreateOrder,
	onNewQuotation,
}: QuotationSummaryFooterProps) {
	void hasSelectedClient;

	return (
		<div className={quotationFooter.shell(compact)}>
			<div className={quotationFooter.row2}>
				<Button
					variant="secondary"
					size="sm"
					onClick={onCopyBaseSummary}
					disabled={!quotationResult}
					icon={copiedFeedback ? CheckCircle2 : Copy}
					className={`${quotationFooter.outline} ${copiedFeedback ? "!text-green-300 !border-green-500/40" : ""}`}
				>
					{copiedFeedback ? "Copiado" : "Copiar orcamento base"}
				</Button>

				<Button
					variant="success"
					size="sm"
					onClick={onCreateOrder}
					disabled={!canCreateOrder || isCreatingOrder}
					icon={CheckCircle2}
					className={quotationFooter.confirm}
				>
					{isCreatingOrder ? "Criando..." : "Criar"}
				</Button>
			</div>

			<Button
				variant="secondary"
				size="sm"
				onClick={onNewQuotation}
				icon={Calculator}
				className={quotationFooter.outline}
			>
				Novo orcamento
			</Button>
		</div>
	);
}

function getVisiblePages(
	totalPages: number,
	currentPage: number,
): Array<number | "..."> {
	const pages: Array<number | "..."> = [];
	const maxVisible = 7;

	if (totalPages <= maxVisible) {
		for (let i = 1; i <= totalPages; i += 1) pages.push(i);
		return pages;
	}

	if (currentPage <= 3) {
		for (let i = 1; i <= 5; i += 1) pages.push(i);
		pages.push("...");
		pages.push(totalPages);
		return pages;
	}

	if (currentPage >= totalPages - 2) {
		pages.push(1);
		pages.push("...");
		for (let i = totalPages - 4; i <= totalPages; i += 1) pages.push(i);
		return pages;
	}

	pages.push(1);
	pages.push("...");
	for (let i = currentPage - 1; i <= currentPage + 1; i += 1) pages.push(i);
	pages.push("...");
	pages.push(totalPages);
	return pages;
}
